/* OpenQwenChat — Sidebar */
import React, { useState, useEffect, useRef } from 'react';
import TherapyHistory from './TherapyHistory.jsx';
import { Ic } from './Icons.jsx';
import HistoryMenu from './HistoryMenu.jsx';
import { TweakTextarea, TweakRadio } from './TweaksPanel.jsx';

export default function Sidebar({
  accountFooter = null, collapsed, onToggle, onNewChat, activeId, history, onSelect,
  systemPrompt = "", defaultSystemPrompt = "", onSystemPromptChange,
  modelId, realtimeMode, onRealtimeModeChange,
  locale = 'en', onNewTherapy, courseCapable = false, therapyRevision, therapyProgress,
  onContinueTherapy, canVideoTherapy = false, onTherapyDeleted, therapyKey, navigationDisabled = false,
  narrow = false, drawerOpen = false, onCloseDrawer,
  onDeleteChat, deleteDisabled = false,
}) {
  const tr = (zh, en) => locale.startsWith('zh') ? zh : en;
  const panel = useRef(null);
  const [showHistory, setShowHistory] = useState(true);
  const [userMenu, setUserMenu] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const navs = [
    { icon: <Ic.Pencil size={20}/>, label: tr('新建聊天', 'New Chat'), action: onNewChat, disabled: navigationDisabled },
    { icon: <Ic.Plus size={20}/>, label: tr('新建咨询', 'New Therapy'), action: onNewTherapy, disabled: navigationDisabled || !courseCapable },
  ];
  useEffect(() => {
    if (!narrow || !drawerOpen) return;
    const previous = document.getElementById('workspace-menu-toggle') || document.activeElement;
    const frame = requestAnimationFrame(() => panel.current?.querySelector('button')?.focus());
    const keydown = event => {
      if (event.defaultPrevented) return;
      // History/settings dialogs may be portalled outside this drawer. Their
      // own focus scope must stay in charge until they close.
      const activeDialog = document.activeElement?.closest('[role="dialog"]');
      if (activeDialog && activeDialog !== panel.current) return;
      if (document.activeElement?.closest('[role="menu"]')) return;
      if (event.key === 'Escape') { event.preventDefault(); onCloseDrawer(); }
      if (event.key !== 'Tab') return;
      const nodes = [...panel.current.querySelectorAll('button, a[href], input, select, textarea, [tabindex="0"]')]
        .filter(node => !node.disabled && node.getClientRects().length);
      const first = nodes[0], last = nodes.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { cancelAnimationFrame(frame); document.removeEventListener('keydown', keydown); previous?.focus?.(); };
  }, [narrow, drawerOpen, onCloseDrawer]);

  // close the avatar menu on any outside click (same pattern as App's model menu)
  useEffect(() => {
    if (!userMenu) return;
    const h = () => setUserMenu(false);
    window.addEventListener("click", h);
    return () => window.removeEventListener("click", h);
  }, [userMenu]);

  return (
    <aside ref={panel} id="workspace-sidebar" className={"sidebar" + (!narrow && collapsed ? " collapsed" : "") + (drawerOpen ? ' drawer-open' : '')}
      role={narrow ? 'dialog' : undefined} aria-modal={narrow && drawerOpen ? true : undefined}
      aria-hidden={narrow && !drawerOpen ? true : undefined} aria-label={tr('工作区导航', 'Workspace navigation')}>
      <div className="sb-top">
        {/* Two files rather than one recoloured by CSS: the wordmark ink is a
            near-black navy that vanishes on the dark palette, while the wave has
            to keep its cyan→teal gradient, so a filter can't serve both. */}
        <div className="brand" title="Lyncia">
          <img className="logo-lockup light" src="/lyncia.png" alt="Lyncia" />
          <img className="logo-lockup dark" src="/lyncia-dark.png" alt="" aria-hidden="true" />
        </div>
        <button className="icon-btn" onClick={onToggle} aria-label={narrow ? tr('关闭侧栏', 'Close sidebar') : collapsed ? tr('展开侧栏', 'Expand sidebar') : tr('收起侧栏', 'Collapse sidebar')}>
          {narrow ? <Ic.Close size={20}/> : <Ic.Panel size={20}/>}
        </button>
      </div>

      <nav className="sb-nav" aria-label={tr('开始对话', 'Start a conversation')}>
        {navs.map((n, i) => (
          <button key={i} className="nav-item" title={n.label} aria-label={n.label} disabled={n.disabled} onClick={n.action}>
            {n.icon}<span>{n.label}</span>
          </button>
        ))}
      </nav>

      <div className="sb-scroll">
      {courseCapable && <TherapyHistory key={therapyKey} model={modelId} locale={locale} revision={therapyRevision} progress={therapyProgress} onContinue={onContinueTherapy} canVideo={canVideoTherapy} onDeleted={onTherapyDeleted} navigationDisabled={navigationDisabled} />}
      <button type="button" className={"sb-section" + (showHistory ? "" : " collapsed")} aria-expanded={showHistory}
           onClick={() => setShowHistory(s => !s)}>
        <span className="label">{tr('聊天记录', 'All chat')}</span>
        <Ic.Chev className="chev" size={16}/>
      </button>

      <div>
        {showHistory && (
          <>
            {history.map(h => (
              <div key={h.id} className="history-row"><HistoryMenu label={tr('聊天操作', 'Chat actions')}
                // One discoverable trigger, hidden until hover/focus and always shown on
                // coarse pointers; right-click, long-press and Shift+F10 keep working.
                button={onDeleteChat ? { className: 'history-more', label: tr('聊天操作', 'Chat actions'),
                  disabled: navigationDisabled || deleteDisabled, icon: <Ic.Dots size={18}/> } : null}
                items={onDeleteChat ? [{label:tr('删除','Delete'), danger:true, disabled:navigationDisabled || deleteDisabled, action:()=>onDeleteChat(h)}] : []}><button
                disabled={navigationDisabled} className={"history-item" + (h.id === activeId ? " active" : "")}
                aria-current={h.id === activeId ? 'page' : undefined} title={h.title}
                onClick={() => onSelect(h.id)}>
                {h.title}
              </button></HistoryMenu></div>
            ))}
            {!history.length && <p className="sidebar-empty">{tr('新的想法，从这里开始。', 'Your conversations will appear here.')}</p>}
          </>
        )}
      </div>

      </div>
      {accountFooter || <div className="sb-user" onClick={(e) => e.stopPropagation()}>
        <button className="sb-user-btn" onClick={() => setUserMenu((o) => !o)}
                aria-haspopup="menu" aria-expanded={userMenu}>
          <div className="avatar">SX</div>
          <span className="name">Shihao Xu</span>
          <Ic.Chev className="chev" size={16}/>
        </button>
        {userMenu && (
          <div className="menu sb-user-menu" role="menu">
            <button className="menu-item" role="menuitem" onClick={() => { setUserMenu(false); setPromptOpen(true); }}>
              <div style={{ flex: 1 }}>
                <div className="mi-name">自定义 System Prompt</div>
                <div className="mi-desc">设置助手的系统提示词（聊天与语音通用）</div>
              </div>
            </button>
          </div>
        )}
      </div>}

      {promptOpen && (
        <SystemPromptModal
          value={systemPrompt}
          defaultValue={defaultSystemPrompt}
          onSave={(v) => { onSystemPromptChange?.(v); setPromptOpen(false); }}
          onClose={() => setPromptOpen(false)}
          modelId={modelId}
          realtimeMode={realtimeMode}
          onRealtimeModeChange={onRealtimeModeChange}
        />
      )}
    </aside>
  );
}

// "通用简洁" preset — the gateway's original DEFAULT_INSTRUCTIONS text, hardcoded
// here on purpose (no backend import). Kept verbatim and confirmed with the user.
const GENERIC_CONCISE_PROMPT =
  "You are Qwen3-Omni, a friendly multimodal assistant. Reply concisely in the user's language.";

// Editor for the assistant system prompt. Edits a local draft so closing
// without saving discards changes; Save commits the textarea content and an
// empty value falls back to the gateway's DEFAULT_INSTRUCTIONS.
//
// A preset selector sits above the textarea: 语音友好 = the TTS-friendly default
// (defaultValue, App's DEFAULT_SYSTEM_PROMPT), 通用简洁 = the concise gateway text.
// Picking a preset fills the textarea; any manual edit that no longer matches a
// preset is shown as 自定义. The active preset is derived from the draft text, so
// it stays correct across reopen without persisting a separate selection.
export function SystemPromptModal({ value, defaultValue, onSave, onClose, modelId, realtimeMode, onRealtimeModeChange }) {
  const [draft, setDraft] = useState(value || "");
  // 语音友好 reuses App's DEFAULT_SYSTEM_PROMPT (passed in as defaultValue) — do
  // not re-type it here. Default selection stays 语音友好 (LIN-28 behavior).
  const presets = [
    { id: "voice", label: "语音友好", text: defaultValue || "" },
    { id: "concise", label: "通用简洁", text: GENERIC_CONCISE_PROMPT },
  ];
  const active = presets.find((p) => p.text === draft)?.id ?? "custom";
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="sp-overlay" onClick={onClose}>
      <div className="sp-modal" role="dialog" aria-modal="true" aria-label="自定义 System Prompt"
           onClick={(e) => e.stopPropagation()}>
        <div className="sp-hd">
          <b>自定义 System Prompt</b>
          <button className="icon-btn" aria-label="Close" onClick={onClose}><Ic.Close size={18}/></button>
        </div>
        <div className="sp-body">
          <div className="sp-presets" role="radiogroup" aria-label="预设">
            {presets.map((p) => (
              <button key={p.id} type="button" role="radio" aria-checked={active === p.id}
                      className={"sp-preset" + (active === p.id ? " on" : "")}
                      onClick={() => setDraft(p.text)}>
                {p.label}
              </button>
            ))}
            <span className={"sp-preset custom" + (active === "custom" ? " on" : "")}
                  role="radio" aria-checked={active === "custom"} aria-disabled="true">
              自定义
            </span>
          </div>
          <TweakTextarea label="System Prompt" value={draft} rows={9}
            placeholder="留空则使用服务器默认提示词"
            onChange={setDraft} />
          <p className="sp-hint">选择预设可快速填入，也可直接编辑（视为「自定义」）。留空将回退到服务器默认提示词。该提示词同时作用于文字聊天与实时语音/数字人。</p>
          {modelId === "qwen3-omni" && (
            <div className="sp-mode">
              <TweakRadio label="对话模式 Mode" value={realtimeMode}
                options={[{ value: "chat", label: "Chat" }, { value: "realtime", label: "Realtime" }]}
                onChange={(v) => onRealtimeModeChange?.(v)} />
              <p className="sp-hint">Chat = 本地上下文 + /v1/chat/completions；Realtime = 上游原生 /v1/realtime。切换后重开一次会话即可生效（立即保存，无需点「保存」）。</p>
            </div>
          )}
        </div>
        <div className="sp-foot">
          <button className="sp-btn ghost" onClick={() => setDraft(defaultValue || "")}>恢复默认</button>
          <div style={{ flex: 1 }} />
          <button className="sp-btn ghost" onClick={onClose}>取消</button>
          <button className="sp-btn primary" onClick={() => onSave(draft)}>保存</button>
        </div>
      </div>
    </div>
  );
}
